import React, { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "../../components/Atomos/Modals/Modal";
import Input from "../../components/Atomos/Inputs/Input";
import CalendarInput from "../../components/Atomos/Inputs/CalendarInput";
import Dropdown from "../../components/Atomos/DropDown/Dropdown";
import TextArea from "../../components/Atomos/Inputs/TextArea";
import InvoicePdfPreview from "../../components/Atomos/PdfPreview/InvoicePdfPreview";
import {
  createInvoice,
  getAccountingFeesForYear,
  getOfficeBillingHistory,
  type BillingHistoryDoc,
  type DuesTier,
  type NewInvoiceItem,
  type NewInvoicePayload,
} from "../../Services/Billing";
import type { AccountingOfficeDetail } from "../../utils/accountingOfficeDetail";
import { getJurisdictionLabel } from "../../utils/jurisdiction/structuredJurisdiction";
import {
  getAccountingOfficeDetail,
  officeDisplayName,
  officeLegalName,
  officePartyKey,
  toFeeContext,
} from "../../utils/accountingOfficeDetail";
import { resolveAccountingOfficeContacts } from "../../utils/accountingContactInheritance";
import { generateAccountingInvoicePDFBlob, buildAccountingInvoiceSectionTitle } from "../../utils/generateAccountingInvoicePDF";
import { buildDefaultCoverLetterBody } from "../../utils/accountingCoverLetter";
import {
  AccountingAssessmentMergedNotice,
  AccountingInvoiceMergeStatusNote,
  AccountingModalCheckbox,
  AccountingModalError,
  AccountingModalFooter,
  AccountingModalHeader,
  AccountingModalSectionTitle,
  AccountingModalSummaryLine,
  AccountingModalSummaryPanel,
  AccountingModalTabs,
  displayToIso,
  formatMoney,
  isoToDisplay,
  MEMBER_BALANCE_CREDIT_LABEL,
  MEMBERSHIP_BALANCE_LABEL,
  todayIso,
} from "./accountingModalUi";
import { fiscalYearFromIso } from "../../utils/dates";
import { BILLING_STATUS, deriveBillingStatus } from "../../utils/billingStatus";
import { getOfficeById } from "../../Services/Office";
import {
  recordInvoiceBalanceMovements,
  resolvePerformedBy,
} from "../../utils/accountingOfficeBalance";
import { useAuth } from "../../store/AuthContext";
import { resolveProfessionalsCountError } from "../../utils/accountingFeesCalculations";
import {
  calculateSpecialFundAmount,
  formatSpecialFundInvoiceDescription,
} from "../../utils/accountingSpecialFund";
import { useInvoiceAuditLogger } from "../../utils/invoiceAudit";
import {
  buildInvoiceConcepts,
  buildInvoiceCreationChanges,
  summarizeConcepts,
} from "../../utils/billingAuditDiff";
import {
  billingPeriodStartIso,
  billingYearForDate,
  membershipDuesFiscalYearForOffice,
} from "../../utils/billingYear";
import { findActiveMembershipDuesInvoice } from "../../utils/billingLedgerUtils";

const PDF_PREVIEW_DEBOUNCE_MS = 400;

type CreateInvoiceModalProps = {
  office: AccountingOfficeDetail;
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
};

type InvoiceTab = "annual" | "assessment";

type AnnualFieldErrors = {
  processingDate: boolean;
  billingPeriod: boolean;
  billingCriteria: boolean;
  discountAmount: boolean;
  specialFund: boolean;
};

const EMPTY_ANNUAL_FIELD_ERRORS: AnnualFieldErrors = {
  processingDate: false,
  billingPeriod: false,
  billingCriteria: false,
  discountAmount: false,
  specialFund: false,
};

const REQUIRED_FIELD_MESSAGE = "This field is required.";

const DISCOUNT_OPTIONS = [0, 5, 10, 15, 20, 25];

function num(value: string): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function formatSignedMoney(value: number): string {
  const amount = Math.abs(Number(value) || 0);
  const formatted = formatMoney(amount);
  return value < 0 ? `-${formatted}` : formatted;
}

function genId(partyKey: string): string {
  const uuid =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${partyKey}-inv-${uuid}`;
}

function tierLabel(t: DuesTier, isInvestmentBanking: boolean): string {
  const label =
    t.label ?? (isInvestmentBanking ? t.range : `${t.range} Professionals`);
  return `${label} (${formatMoney(t.fee)})`;
}

function clampToTodayOrBefore(iso: string): string {
  const today = todayIso();
  return iso > today ? today : iso;
}

function validateAnnualInvoiceFields(input: {
  processingDate: string;
  billingPeriod: string;
  tierRange: string;
  specialFund: string;
  feesLoading: boolean;
}): string | null {
  if (input.feesLoading) {
    return "Fee catalog is still loading. Please wait.";
  }
  if (!input.processingDate.trim()) {
    return "Processing Date is required.";
  }
  if (input.processingDate > todayIso()) {
    return "Processing Date cannot be in the future.";
  }
  if (!input.billingPeriod.trim()) {
    return "Billing Period is required.";
  }
  if (!input.tierRange.trim()) {
    return "Billing Criteria is required.";
  }
  if (input.specialFund.trim() === "") {
    return "Special Fund is required.";
  }
  return null;
}

export default function CreateInvoiceModal({
  office,
  isOpen,
  onClose,
  onCreated,
}: CreateInvoiceModalProps): React.ReactElement {
  const officeName = officeDisplayName(office);
  const firmName = officeLegalName(office);
  const jurisdiction = getJurisdictionLabel(office);
  const partyKey = officePartyKey(office.id);

  const [officeBalance, setOfficeBalance] = useState(
    Number(office.balance ?? 0),
  );
  const availableCredit = Math.max(0, -officeBalance);
  const availableDebt = Math.max(0, officeBalance);
  const creditIsDebt = officeBalance > 0;
  const hasBalanceToApply = availableCredit > 0 || availableDebt > 0;

  const { logEvent } = useInvoiceAuditLogger();
  const { loggedPerson, tokenDecoded } = useAuth();

  const [officeInvoices, setOfficeInvoices] = useState<BillingHistoryDoc[]>([]);
  const [tab, setTab] = useState<InvoiceTab>("annual");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [tiers, setTiers] = useState<DuesTier[]>([]);
  const [isInvestmentBanking, setIsInvestmentBanking] = useState(false);
  const [feesLoading, setFeesLoading] = useState(false);
  const [processingDateError, setProcessingDateError] = useState(false);
  const [billingPeriodError, setBillingPeriodError] = useState(false);
  const [billingCriteriaError, setBillingCriteriaError] = useState(false);
  const [specialFundError, setSpecialFundError] = useState(false);

  const [processingDate, setProcessingDate] = useState(todayIso());
  const [tierRange, setTierRange] = useState<string>("");
  const [discountPct, setDiscountPct] = useState(0);
  const [specialFund, setSpecialFund] = useState("0");
  const [specialFundName, setSpecialFundName] = useState("Special Fund");
  const [specialFundPercentage, setSpecialFundPercentage] = useState(10);
  const [annualMeetingFee, setAnnualMeetingFee] = useState("0");
  const [includeBalance, setIncludeBalance] = useState(false);
  const [balance, setBalance] = useState("0");
  const [lateFees, setLateFees] = useState("0");
  const [includeInitiation, setIncludeInitiation] = useState(false);
  const [initiationFee, setInitiationFee] = useState("0");
  const [includeAssessment, setIncludeAssessment] = useState(false);
  const [assessmentFee, setAssessmentFee] = useState("0");
  const [addNote, setAddNote] = useState(false);
  const [note, setNote] = useState("");
  const [coverLetterBody, setCoverLetterBody] = useState("");
  const [coverLetterDirty, setCoverLetterDirty] = useState(false);
  const [billingContactName, setBillingContactName] = useState(officeName);

  const [assessmentDate, setAssessmentDate] = useState(todayIso());
  const [baseAssessment, setBaseAssessment] = useState("0");
  const [addCustomCharges, setAddCustomCharges] = useState(false);
  const [customCharges, setCustomCharges] = useState<
    { description: string; amount: string }[]
  >([{ description: "", amount: "0" }]);
  const [annualFieldErrors, setAnnualFieldErrors] = useState<AnnualFieldErrors>(
    EMPTY_ANNUAL_FIELD_ERRORS,
  );

  const feeFiscalYear = useMemo(
    () =>
      membershipDuesFiscalYearForOffice(
        processingDate,
        office.billingStartDate,
      ),
    [processingDate, office.billingStartDate],
  );

  const billingPeriod = useMemo(
    () =>
      billingPeriodStartIso(
        billingYearForDate(processingDate, office.billingStartDate),
        office.billingStartDate,
      ),
    [processingDate, office.billingStartDate],
  );

  const existingMembershipDues = useMemo(
    () => findActiveMembershipDuesInvoice(officeInvoices, feeFiscalYear),
    [officeInvoices, feeFiscalYear],
  );
  const annualTabBlocked = Boolean(existingMembershipDues);

  const todayMaxDate = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const tierOptions = useMemo(
    () =>
      tiers.map((t) => ({
        id: t.range,
        name: tierLabel(t, isInvestmentBanking),
      })),
    [tiers, isInvestmentBanking],
  );

  const discountOptions = useMemo(
    () =>
      DISCOUNT_OPTIONS.map((p) => ({
        id: String(p),
        name: p === 0 ? "No Discount" : `${p}%`,
      })),
    [],
  );

  const selectedTierName =
    tierOptions.find((t) => t.id === tierRange)?.name ?? "";

  const selectedDiscountName =
    discountOptions.find((o) => Number(o.id) === discountPct)?.name ??
    "No Discount";

  const professionalsCountError = useMemo(
    () => resolveProfessionalsCountError(toFeeContext(office)),
    [office],
  );
  const formDisabled = Boolean(professionalsCountError);

  const applyFeesFromCatalog = (
    fees: Awaited<ReturnType<typeof getAccountingFeesForYear>>,
  ) => {
    setTiers(fees.duesTiers);
    setIsInvestmentBanking(fees.isInvestmentBanking);
    setSpecialFundName(fees.specialFundName);
    setSpecialFundPercentage(fees.specialFundPercentage);
    const range = fees.defaultTierRange || fees.duesTiers[0]?.range || "";
    setTierRange(range);
    const tier = fees.duesTiers.find((t) => t.range === range);
    const base = tier?.fee ?? 0;
    setSpecialFund(
      calculateSpecialFundAmount(base, fees.specialFundPercentage).toString(),
    );
    setAnnualMeetingFee(String(fees.annualMeetingRegFee));
    setInitiationFee((fees.initiationByRange[range] ?? 0).toString());
    setBillingCriteriaError(false);
    setSpecialFundError(false);
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setSaving(false);
    setError("");
    setProcessingDateError(false);
    setBillingPeriodError(false);
    setBillingCriteriaError(false);
    setSpecialFundError(false);
    setProcessingDate(todayIso());
    setDiscountPct(0);
    setAnnualMeetingFee("0");
    setIncludeBalance(false);
    setLateFees("0");
    setIncludeInitiation(false);
    setIncludeAssessment(false);
    setAssessmentFee("0");
    setAddNote(false);
    setNote("");
    setCoverLetterBody("");
    setCoverLetterDirty(false);
    setAssessmentDate(todayIso());
    setBaseAssessment("0");
    setAddCustomCharges(false);
    setCustomCharges([{ description: "", amount: "0" }]);
    setAnnualFieldErrors(EMPTY_ANNUAL_FIELD_ERRORS);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    let cancelled = false;
    void (async () => {
      const docs = await getOfficeBillingHistory(office.id);
      if (cancelled) {
        return;
      }
      setOfficeInvoices(docs);
      const defaultFiscalYear = membershipDuesFiscalYearForOffice(
        todayIso(),
        office.billingStartDate,
      );
      if (findActiveMembershipDuesInvoice(docs, defaultFiscalYear)) {
        setTab("assessment");
      } else {
        setTab("annual");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, office.id, office.billingStartDate]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    let cancelled = false;
    (async () => {
      const raw = await getOfficeById(office.id);
      if (cancelled) {
        return;
      }
      const fresh = Number((raw?.balance ?? office.balance) ?? 0);
      setOfficeBalance(fresh);
      setBalance(String(Math.abs(fresh)));
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, office.id, office.balance]);

  const feeOfficeKey = useMemo(
    () =>
      [
        office.id,
        String(office.serviceType ?? ""),
        office.firmProfessionalsInPrimary,
        office.professionalsInPrimary,
        office.countOfPeople,
      ].join("|"),
    [
      office.id,
      office.serviceType,
      office.firmProfessionalsInPrimary,
      office.professionalsInPrimary,
      office.countOfPeople,
    ],
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let cancelled = false;
    setFeesLoading(true);
    (async () => {
      try {
        const officeForFees =
          (await getAccountingOfficeDetail(office.id)) ?? office;
        const fees = await getAccountingFeesForYear(
          feeFiscalYear,
          toFeeContext(officeForFees),
        );
        if (cancelled) {
          return;
        }
        if (fees.duesTiers.length === 0) {
          setError(
            "No membership fee tiers found for this office service type and fiscal year.",
          );
          setTierRange("");
          setBillingCriteriaError(true);
          return;
        }
        setError("");
        applyFeesFromCatalog(fees);
      } catch {
        if (!cancelled) {
          setAnnualFieldErrors((prev) => ({
            ...prev,
            billingCriteria: true,
            discountAmount: true,
          }));
          setBillingCriteriaError(true);
        }
      } finally {
        if (!cancelled) {
          setFeesLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, feeFiscalYear, feeOfficeKey, office]);

  const baseDues = useMemo(
    () => tiers.find((t) => t.range === tierRange)?.fee ?? 0,
    [tiers, tierRange],
  );
  const discountAmount = useMemo(
    () => Math.round(baseDues * (discountPct / 100) * 100) / 100,
    [baseDues, discountPct],
  );
  const netDues = Math.max(0, baseDues - discountAmount);
  const annualMeetingAmount = num(annualMeetingFee);
  const lateFeesAmount = num(lateFees);
  const initiationAmount = includeInitiation ? num(initiationFee) : 0;
  const assessmentAmount = includeAssessment ? num(assessmentFee) : 0;
  const isAssessmentMergedWithAnnual = includeAssessment;

  const mergedAnnualInvoiceRef = useMemo(
    () => `${feeFiscalYear}-DRAFT`,
    [feeFiscalYear],
  );

  const mergedAnnualInvoiceDate = useMemo(
    () => isoToDisplay(processingDate),
    [processingDate],
  );

  useEffect(() => {
    if (!isAssessmentMergedWithAnnual) {
      return;
    }
    setBaseAssessment(assessmentFee);
    setAssessmentDate(processingDate);
    setAddCustomCharges(false);
  }, [
    isAssessmentMergedWithAnnual,
    assessmentFee,
    processingDate,
  ]);

  const annualSubtotal = useMemo(() => {
    return (
      netDues +
      num(specialFund) +
      annualMeetingAmount +
      lateFeesAmount +
      initiationAmount +
      assessmentAmount
    );
  }, [
    netDues,
    specialFund,
    annualMeetingAmount,
    lateFeesAmount,
    initiationAmount,
    assessmentAmount,
  ]);

  // Balance application:
  // - Credit (negative office balance): the applied amount is a payment/discount
  //   that reduces the invoice. Capped by the invoice charges subtotal and the
  //   available credit.
  // - Debt (positive office balance): the applied amount is folded into this
  //   invoice as an extra charge. Capped only by the outstanding balance.
  const maxBalanceApplicable = creditIsDebt
    ? availableDebt
    : Math.max(0, Math.min(availableCredit, annualSubtotal));

  const appliedBalance = includeBalance
    ? Math.max(0, Math.min(num(balance), maxBalanceApplicable))
    : 0;
  const appliedCredit = creditIsDebt ? 0 : appliedBalance;
  const appliedDebt = creditIsDebt ? appliedBalance : 0;

  const annualTotal = useMemo(
    () => Math.round((annualSubtotal + appliedDebt) * 100) / 100,
    [annualSubtotal, appliedDebt],
  );
  const netDue = annualTotal - appliedCredit;

  const handleCreditBalanceChange = (raw: string) => {
    if (raw.trim() === "") {
      setBalance("");
      return;
    }
    const clamped = Math.max(0, Math.min(num(raw), maxBalanceApplicable));
    setBalance(String(clamped));
  };

  // Keep the balance input capped once the caps are known (e.g. after fees load,
  // or when the tier/total changes). Only reduce the value so we never fight the
  // async initialization that seeds the field.
  useEffect(() => {
    if (maxBalanceApplicable <= 0) {
      return;
    }
    if (num(balance) > maxBalanceApplicable) {
      setBalance(String(maxBalanceApplicable));
    }
  }, [balance, maxBalanceApplicable]);

  const customChargesTotal = useMemo(
    () =>
      addCustomCharges
        ? customCharges.reduce((s, c) => s + num(c.amount), 0)
        : 0,
    [addCustomCharges, customCharges],
  );
  const assessmentTotal = num(baseAssessment) + customChargesTotal;

  const buildAnnualItems = useCallback((): NewInvoiceItem[] => {
    const items: NewInvoiceItem[] = [];
    if (baseDues > 0) {
      items.push({
        code: "annual",
        description: "Annual membership dues",
        amount: baseDues,
      });
    }
    if (discountAmount > 0) {
      items.push({
        code: "discount",
        description: `Member discount (${discountPct}%)`,
        amount: -discountAmount,
      });
    }
    if (num(specialFund) > 0) {
      items.push({
        code: "special",
        description: formatSpecialFundInvoiceDescription({
          specialFundName,
          specialFundPercentage,
        }),
        amount: num(specialFund),
      });
    }
    if (num(annualMeetingFee) > 0) {
      items.push({
        code: "annualMeeting",
        description: "Annual meeting registration fee",
        amount: num(annualMeetingFee),
      });
    }
    if (num(lateFees) > 0) {
      items.push({
        code: "late",
        description: "Late fees",
        amount: num(lateFees),
      });
    }
    if (includeInitiation && num(initiationFee) > 0) {
      items.push({
        code: "initiation",
        description: "Initiation fee",
        amount: num(initiationFee),
      });
    }
    if (includeAssessment && num(assessmentFee) > 0) {
      items.push({
        code: "assessment",
        description: "Assessment fee",
        amount: num(assessmentFee),
      });
    }
    if (appliedDebt > 0) {
      items.push({
        code: "previousBalance",
        description: MEMBERSHIP_BALANCE_LABEL,
        amount: appliedDebt,
      });
    }
    return items;
  }, [
    baseDues,
    discountAmount,
    discountPct,
    specialFund,
    specialFundName,
    specialFundPercentage,
    annualMeetingFee,
    lateFees,
    includeInitiation,
    initiationFee,
    includeAssessment,
    assessmentFee,
    appliedDebt,
  ]);

  const buildAssessmentItems = useCallback((): NewInvoiceItem[] => {
    const items: NewInvoiceItem[] = [];
    if (num(baseAssessment) > 0) {
      items.push({
        code: "assessment",
        description: "Base assessment",
        amount: num(baseAssessment),
      });
    }
    if (addCustomCharges) {
      for (const c of customCharges) {
        if (num(c.amount) > 0) {
          items.push({
            code: "other",
            description: c.description.trim() || "Custom charge",
            amount: num(c.amount),
          });
        }
      }
    }
    return items;
  }, [baseAssessment, addCustomCharges, customCharges]);

  const previewInvoice = useMemo((): BillingHistoryDoc | null => {
    if (tab === "annual") {
      const items = buildAnnualItems();
      if (items.length === 0) {
        return null;
      }

      const fiscalYear = feeFiscalYear;
      const total = items.reduce(
        (sum, item) => sum + item.amount * (item.quantity ?? 1),
        0,
      );

      return {
        id: `preview-${partyKey}`,
        type: "invoice",
        partyKey,
        partyId: office.id,
        currency: "USD",
        invoiceNumber: `${fiscalYear}-DRAFT`,
        context: { kind: "membershipDues", fiscalYear },
        issueDate: processingDate,
        dueDate: billingPeriod || undefined,
        items,
        total,
        totalPaid: appliedCredit,
        balance: total - appliedCredit,
        status: deriveBillingStatus(total, appliedCredit),
        creditApplied: appliedCredit,
        memo: addNote && note.trim() ? note.trim() : undefined,
        coverLetterBody: coverLetterBody.trim() || undefined,
        timesSent: 0,
      };
    }

    const items = buildAssessmentItems();
    if (items.length === 0) {
      return null;
    }

    const fiscalYear = fiscalYearFromIso(assessmentDate);
    const total = items.reduce((sum, item) => sum + item.amount, 0);

    return {
      id: `preview-${partyKey}`,
      type: "invoice",
      partyKey,
      partyId: office.id,
      currency: "USD",
      invoiceNumber: `${fiscalYear}-DRAFT`,
      context: { kind: "assessment", fiscalYear },
      issueDate: assessmentDate,
      items,
      total,
      totalPaid: 0,
      balance: total,
      status: BILLING_STATUS.unpaid,
      coverLetterBody: coverLetterBody.trim() || undefined,
      timesSent: 0,
    };
  }, [
    tab,
    partyKey,
    office.id,
    buildAnnualItems,
    buildAssessmentItems,
    feeFiscalYear,
    billingPeriod,
    processingDate,
    assessmentDate,
    addNote,
    note,
    appliedCredit,
    coverLetterBody,
  ]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let cancelled = false;
    void (async () => {
      const resolution = await resolveAccountingOfficeContacts(office).catch(
        () => null,
      );
      if (cancelled) {
        return;
      }
      setBillingContactName(
        resolution?.accountingContactName ||
          resolution?.billingRecipients.primaryName ||
          officeName,
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, office, officeName]);

  useEffect(() => {
    if (!isOpen || !previewInvoice || coverLetterDirty) {
      return;
    }
    const subject = buildAccountingInvoiceSectionTitle(previewInvoice);
    setCoverLetterBody(buildDefaultCoverLetterBody(subject, billingContactName));
  }, [isOpen, previewInvoice, coverLetterDirty, billingContactName]);

  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfError, setPdfError] = useState("");

  useEffect(() => {
    if (!isOpen || !previewInvoice) {
      setPdfBlob(null);
      setPdfGenerating(false);
      setPdfError("");
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      void (async () => {
        setPdfGenerating(true);
        setPdfError("");
        try {
          const blob = await generateAccountingInvoicePDFBlob({
            invoice: previewInvoice,
            office,
            memoOverride: previewInvoice.memo,
          });
          if (cancelled) {
            return;
          }
          setPdfBlob(blob);
        } catch {
          if (!cancelled) {
            setPdfError("Unable to load invoice PDF preview.");
            setPdfBlob(null);
          }
        } finally {
          if (!cancelled) {
            setPdfGenerating(false);
          }
        }
      })();
    }, PDF_PREVIEW_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [isOpen, previewInvoice, office]);

  const buildPayload = (): NewInvoicePayload | null => {
    if (professionalsCountError) {
      setError(professionalsCountError);
      return null;
    }

    if (tab === "annual") {
      if (annualTabBlocked) {
        const invoiceRef =
          existingMembershipDues?.invoiceNumber?.trim() || "existing invoice";
        setError(
          `A membership dues invoice already exists for fiscal year ${feeFiscalYear} (${invoiceRef}). Only assessment invoices can be created until the next office fiscal year.`,
        );
        return null;
      }

      const validationError = validateAnnualInvoiceFields({
        processingDate,
        billingPeriod,
        tierRange,
        specialFund,
        feesLoading,
      });
      if (validationError) {
        setError(validationError);
        setProcessingDateError(
          !processingDate.trim() || processingDate > todayIso(),
        );
        setBillingPeriodError(!billingPeriod.trim());
        setBillingCriteriaError(!tierRange.trim());
        setSpecialFundError(specialFund.trim() === "");
        return null;
      }

      const items = buildAnnualItems();

      if (items.length === 0) {
        setError("Add at least one charge before saving.");
        return null;
      }

      const fiscalYear = feeFiscalYear;
      const total =
        Math.round(
          items.reduce((s, it) => s + it.amount * (it.quantity ?? 1), 0) * 100,
        ) / 100;
      return {
        type: "invoice",
        partyKey,
        partyType: "office",
        partyId: office.id,
        officeId: office.id,
        currency: "USD",
        invoiceNumber: `${fiscalYear}-${String(Date.now()).slice(-5)}`,
        context: { kind: "membershipDues", fiscalYear },
        issueDate: processingDate,
        dueDate: billingPeriod || undefined,
        items,
        total,
        totalPaid: appliedCredit,
        balance: total - appliedCredit,
        status: deriveBillingStatus(total, appliedCredit),
        creditApplied: appliedCredit,
        memo: addNote && note.trim() ? note.trim() : undefined,
        coverLetterBody: coverLetterBody.trim() || undefined,
        timesSent: 0,
      };
    }

    const items = buildAssessmentItems();
    if (items.length === 0) {
      setError("Enter a base assessment amount before saving.");
      return null;
    }
    const fiscalYear = fiscalYearFromIso(assessmentDate);
    const total = items.reduce((s, it) => s + it.amount, 0);
    return {
      type: "invoice",
      partyKey,
      partyType: "office",
      partyId: office.id,
      officeId: office.id,
      currency: "USD",
      invoiceNumber: `${fiscalYear}-${String(Date.now()).slice(-5)}`,
      context: { kind: "assessment", fiscalYear },
      issueDate: assessmentDate,
      items,
      total,
      totalPaid: 0,
      balance: total,
      status: BILLING_STATUS.unpaid,
      coverLetterBody: coverLetterBody.trim() || undefined,
      timesSent: 0,
    };
  };

  const handleSave = async () => {
    setError("");
    const payload = buildPayload();
    if (!payload) {
      return;
    }
    setSaving(true);
    try {
      const invoiceId = genId(partyKey);
      const createdInvoice = { ...payload, id: invoiceId };
      await createInvoice(createdInvoice);
      const creationChanges = buildInvoiceCreationChanges(createdInvoice);
      const creationConcepts = buildInvoiceConcepts(createdInvoice);
      const conceptSummary = summarizeConcepts(creationConcepts);
      void logEvent({
        billingDocId: invoiceId,
        billingDocType: "invoice",
        partyKey,
        partyId: office.id,
        officeId: office.id,
        firmId: String(office.firm ?? "") || null,
        invoiceNumber: payload.invoiceNumber,
        contextKind: payload.context.kind,
        action: "created",
        changes: creationChanges,
        concepts: creationConcepts,
        detail: conceptSummary
          ? `total ${payload.total} ${payload.currency}, status ${payload.status} · ${conceptSummary}`
          : `total ${payload.total} ${payload.currency}, status ${payload.status}`,
      });
      // Fold the applied balance into this invoice: credit consumed moves the
      // office balance up (toward 0 from a negative credit); debt charged moves
      // it down (toward 0 from a positive debt).
      const balanceAdjustment = appliedCredit - appliedDebt;
      if (tab === "annual" && balanceAdjustment !== 0) {
        const { performedById, performedByName } = resolvePerformedBy(
          loggedPerson,
          tokenDecoded,
        );
        await recordInvoiceBalanceMovements({
          officeId: office.id,
          firmId: String(office.firm ?? "") || null,
          invoiceId,
          invoiceRef: payload.invoiceNumber ?? invoiceId,
          appliedCredit,
          appliedDebt,
          performedById,
          performedByName,
        });
      }
      onCreated();
      onClose();
    } catch {
      setError("Unable to save invoice. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isModalOpen={isOpen}
      toggleModal={onClose}
      title="Invoice"
      titleColor="text-primary font-decimal text-2xl font-medium leading-8"
      compact
      compactBodyScrolls
      className="max-w-6xl"
    >
      <div className="flex flex-col gap-5">
        <AccountingModalHeader
          firmName={firmName}
          jurisdiction={jurisdiction}
        />

        <AccountingModalTabs
          tabs={[
            { key: "annual", label: "Annual Invoice" },
            { key: "assessment", label: "Assessments Invoice" },
          ]}
          active={tab}
          onChange={(key) => {
            if (formDisabled) {
              return;
            }
            setTab(key);
            setError("");
            setAnnualFieldErrors(EMPTY_ANNUAL_FIELD_ERRORS);
          }}
        />

        {professionalsCountError ? (
          <AccountingModalError message={professionalsCountError} />
        ) : null}

        {annualTabBlocked ? (
          <div className="rounded border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
            <span className="font-medium">Notice:</span> A membership dues invoice
            already exists for fiscal year {feeFiscalYear}
            {existingMembershipDues?.invoiceNumber
              ? ` (${existingMembershipDues.invoiceNumber})`
              : ""}
            . Only assessment invoices can be created until the next office fiscal
            year.
          </div>
        ) : null}

        {tab === "annual" ? (
          <div
            className={`flex flex-col gap-5 ${formDisabled ? "pointer-events-none opacity-60" : ""}`}
          >
            {isAssessmentMergedWithAnnual && <AccountingAssessmentMergedNotice />}

            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
              <CalendarInput
                title="Processing Date"
                value={isoToDisplay(processingDate)}
                defaultValue={isoToDisplay(processingDate)}
                onChange={(date: string) => {
                  setProcessingDate(clampToTodayOrBefore(displayToIso(date)));
                  setProcessingDateError(false);
                }}
                type="single"
                required
                maxDate={new Date()}
                error={processingDateError}
              />
              <CalendarInput
                title={`Billing Period (${feeFiscalYear - 1}-${feeFiscalYear})`}
                value={isoToDisplay(billingPeriod)}
                defaultValue={isoToDisplay(billingPeriod)}
                onChange={() => {}}
                type="single"
                required
                disabled
                error={billingPeriodError}
              />

              <Dropdown
                title="Billing Criteria"
                data={tierOptions}
                selectedObjst={selectedTierName}
                getid
                disableSort
                required
                disabled
                disableInput
                seter={() => {}}
                error={billingCriteriaError}
              />
              <div className="flex flex-col gap-4">
                <Dropdown
                  title="Member Discount (%)"
                  data={discountOptions}
                  selectedObjst={selectedDiscountName}
                  getid
                  disableSort
                  seter={(selected: { id?: string } | null) => {
                    setDiscountPct(Number(selected?.id ?? 0));
                  }}
                  error={false}
                />
                <Input
                  title="Discount Amount"
                  value={formatMoney(discountAmount)}
                  required
                  disabled
                  error={annualFieldErrors.discountAmount}
                  errorMsj={
                    annualFieldErrors.discountAmount
                      ? REQUIRED_FIELD_MESSAGE
                      : undefined
                  }
                />
              </div>

              <Input
                title={`${specialFundName} (${specialFundPercentage}% Base)`}
                type="number"
                value={specialFund}
                required
                disabled
                error={specialFundError}
              />
              <Input
                title="Annual Meeting Reg. Fee"
                type="number"
                value={annualMeetingFee}
                onChange={(e) => setAnnualMeetingFee(e.target.value)}
                error={false}
              />
              <div className="flex flex-col gap-3">
                <AccountingModalCheckbox
                  checked={includeBalance}
                  onChange={setIncludeBalance}
                  label={
                    creditIsDebt
                      ? `Apply ${MEMBERSHIP_BALANCE_LABEL}`
                      : `Apply ${MEMBER_BALANCE_CREDIT_LABEL}`
                  }
                  disabled={!hasBalanceToApply}
                />
                <Input
                  title={`${creditIsDebt ? MEMBERSHIP_BALANCE_LABEL : MEMBER_BALANCE_CREDIT_LABEL} (${formatSignedMoney(officeBalance)})`}
                  type="number"
                  value={balance}
                  disabled={!includeBalance}
                  onChange={(e) => handleCreditBalanceChange(e.target.value)}
                  error={false}
                />
                {creditIsDebt && (
                  <p className="text-xs text-gray-500">
                    Firm has an outstanding balance; the applied amount will be added
                    as an extra charge on this invoice.
                  </p>
                )}
              </div>
              <Input
                title="Late Fees"
                type="number"
                value={lateFees}
                onChange={(e) => setLateFees(e.target.value)}
                error={false}
              />

              <div className="flex flex-col gap-3">
                <AccountingModalCheckbox
                  checked={includeInitiation}
                  onChange={setIncludeInitiation}
                  label="Include Initiation Fee"
                />
                <Input
                  title="Initiation Fee"
                  type="number"
                  value={initiationFee}
                  disabled
                  error={false}
                />
              </div>
              <div className="flex flex-col gap-3">
                <AccountingModalCheckbox
                  checked={includeAssessment}
                  onChange={setIncludeAssessment}
                  label="Include Assessment Fee"
                />
                <Input
                  title="Assessment Fee"
                  type="number"
                  value={assessmentFee}
                  onChange={(e) => setAssessmentFee(e.target.value)}
                  disabled={!includeAssessment}
                  error={false}
                />
                {includeAssessment && (
                  <p className="text-xs text-gray-500">
                    This assessment amount will also be updated on the office&apos;s
                    assessment invoice.
                  </p>
                )}
              </div>
            </div>

            <AccountingModalSummaryPanel>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <AccountingModalSummaryLine
                  label="Annual Dues:"
                  value={formatMoney(baseDues)}
                />
                {discountAmount > 0 && (
                  <AccountingModalSummaryLine
                    label={`Member Discount (${discountPct}%):`}
                    value={`-${formatMoney(discountAmount)}`}
                  />
                )}
                <AccountingModalSummaryLine
                  label={`${specialFundName}:`}
                  value={formatMoney(num(specialFund))}
                />
                {annualMeetingAmount > 0 && (
                  <AccountingModalSummaryLine
                    label="Annual Meeting Reg. Fee:"
                    value={formatMoney(annualMeetingAmount)}
                  />
                )}
                {lateFeesAmount > 0 && (
                  <AccountingModalSummaryLine
                    label="Late Fees:"
                    value={formatMoney(lateFeesAmount)}
                  />
                )}
                {initiationAmount > 0 && (
                  <AccountingModalSummaryLine
                    label="Initiation Fee:"
                    value={formatMoney(initiationAmount)}
                  />
                )}
                {assessmentAmount > 0 && (
                  <AccountingModalSummaryLine
                    label="Assessment Fee:"
                    value={formatMoney(assessmentAmount)}
                  />
                )}
                {appliedDebt > 0 && (
                  <AccountingModalSummaryLine
                    label={`${MEMBERSHIP_BALANCE_LABEL}:`}
                    value={formatMoney(appliedDebt)}
                  />
                )}
              </div>
              <div className="mt-3 border-t border-gray-200 pt-3">
                <AccountingModalSummaryLine
                  label="Annual Dues (Net):"
                  value={formatMoney(netDues)}
                />
                <AccountingModalSummaryLine
                  label="Total Annual Dues:"
                  value={formatMoney(annualTotal)}
                  strong
                />
                {appliedCredit > 0 && (
                  <>
                    <AccountingModalSummaryLine
                      label={`${MEMBER_BALANCE_CREDIT_LABEL}:`}
                      value={`-${formatMoney(appliedCredit)}`}
                    />
                    <AccountingModalSummaryLine
                      label="Total Due:"
                      value={formatMoney(netDue)}
                      strong
                    />
                  </>
                )}
              </div>
            </AccountingModalSummaryPanel>

            <div className="flex flex-col gap-3 border-t border-gray-200 pt-3">
              <AccountingModalCheckbox
                checked={addNote}
                onChange={setAddNote}
                label="Add Note (max 90 char. including space)"
              />
              {addNote && (
                <Input
                  title="Note"
                  value={note}
                  onChange={(e) => setNote(e.target.value.slice(0, 90))}
                  error={false}
                  placeholder="Note..."
                />
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {isAssessmentMergedWithAnnual && (
              <AccountingInvoiceMergeStatusNote
                annualInvoiceRef={mergedAnnualInvoiceRef}
                mergeDate={mergedAnnualInvoiceDate}
              />
            )}
            <div
              className={`flex flex-col gap-5 ${isAssessmentMergedWithAnnual ? "pointer-events-none opacity-60" : formDisabled ? "pointer-events-none opacity-60" : ""}`}
            >
            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
              <CalendarInput
                title="Date"
                value={isoToDisplay(assessmentDate)}
                defaultValue={isoToDisplay(assessmentDate)}
                onChange={(date: string) =>
                  setAssessmentDate(displayToIso(date))
                }
                type="single"
                disabled={isAssessmentMergedWithAnnual}
                error={false}
              />
              <Input
                title="Base Assessment Amount"
                type="number"
                required
                value={baseAssessment}
                onChange={(e) => setBaseAssessment(e.target.value)}
                disabled={isAssessmentMergedWithAnnual}
                error={false}
              />
            </div>

            <div className="border-t border-gray-200 pt-3">
              <AccountingModalCheckbox
                checked={addCustomCharges}
                onChange={setAddCustomCharges}
                label="Add Custom Charges"
                disabled={isAssessmentMergedWithAnnual}
              />
              {addCustomCharges && (
                <div className="mt-3 flex flex-col gap-3">
                  {customCharges.map((c, idx) => (
                    <div key={idx} className="flex items-end gap-3">
                      <div className="min-w-0 flex-1">
                        <Input
                          title="Description"
                          value={c.description}
                          placeholder="Description"
                          onChange={(e) =>
                            setCustomCharges((prev) =>
                              prev.map((p, i) =>
                                i === idx
                                  ? { ...p, description: e.target.value }
                                  : p,
                              ),
                            )
                          }
                          disabled={isAssessmentMergedWithAnnual}
                          error={false}
                        />
                      </div>
                      <div className="w-40 shrink-0">
                        <Input
                          title="Amount"
                          type="number"
                          value={c.amount}
                          onChange={(e) =>
                            setCustomCharges((prev) =>
                              prev.map((p, i) =>
                                i === idx
                                  ? { ...p, amount: e.target.value }
                                  : p,
                              ),
                            )
                          }
                          disabled={isAssessmentMergedWithAnnual}
                          error={false}
                        />
                      </div>
                      {!isAssessmentMergedWithAnnual && (
                        <LegacyButtonInlineRemove
                          onClick={() =>
                            setCustomCharges((prev) =>
                              prev.length === 1
                                ? prev
                                : prev.filter((_, i) => i !== idx),
                            )
                          }
                        />
                      )}
                    </div>
                  ))}
                  {!isAssessmentMergedWithAnnual && (
                    <LegacyButtonInlineAdd
                      onClick={() =>
                        setCustomCharges((prev) => [
                          ...prev,
                          { description: "", amount: "0" },
                        ])
                      }
                    />
                  )}
                </div>
              )}
            </div>

            <AccountingModalSummaryPanel>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <AccountingModalSummaryLine
                  label="Base Assessment:"
                  value={formatMoney(num(baseAssessment))}
                />
                <AccountingModalSummaryLine
                  label="Custom Charges:"
                  value={formatMoney(customChargesTotal)}
                />
              </div>
              <div className="mt-3 border-t border-gray-200 pt-3">
                <AccountingModalSummaryLine
                  label="Total Assessment Due:"
                  value={formatMoney(assessmentTotal)}
                  strong
                />
              </div>
            </AccountingModalSummaryPanel>
            </div>
          </div>
        )}

        <div>
          <AccountingModalSectionTitle>Cover Letter</AccountingModalSectionTitle>
          <div className="mt-3">
            <TextArea
              title="Cover Letter Body"
              value={coverLetterBody}
              onChange={(e) => {
                setCoverLetterDirty(true);
                setCoverLetterBody(e.target.value);
              }}
              height="10rem"
              error={false}
            />
          </div>
        </div>

        <div>
          <AccountingModalSectionTitle>Invoice PDF Preview</AccountingModalSectionTitle>
          <div className="mt-3 overflow-hidden rounded border border-gray-200 bg-gray-50">
            {pdfError ? (
              <div className="flex min-h-[28rem] items-center justify-center px-4 text-center text-sm text-red-600">
                {pdfError}
              </div>
            ) : (
              <InvoicePdfPreview
                pdfBlob={pdfBlob}
                loading={pdfGenerating}
                loadingMessage="Generating invoice preview..."
                emptyMessage="Add charges to see the invoice preview."
              />
            )}
          </div>
        </div>

        <AccountingModalError message={error} />

        <AccountingModalFooter
          onCancel={onClose}
          onSubmit={handleSave}
          submitText="Save"
          saving={saving}
          submitDisabled={
            formDisabled ||
            (tab === "annual" && annualTabBlocked) ||
            (tab === "assessment" && isAssessmentMergedWithAnnual)
          }
        />
      </div>
    </Modal>
  );
}

function LegacyButtonInlineRemove({
  onClick,
}: {
  onClick: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      className="pb-2 text-base text-primary hover:underline"
      onClick={onClick}
    >
      Remove
    </button>
  );
}

function LegacyButtonInlineAdd({
  onClick,
}: {
  onClick: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      className="self-start text-base font-medium text-primary hover:underline"
      onClick={onClick}
    >
      + Add charge
    </button>
  );
}
